import { Code, CopyChip, Flex, ProviderIcon, Table, Text } from '@workos-inc/design-system';
import * as React from 'react';
type ProviderIconType = React.ComponentPropsWithoutRef<typeof ProviderIcon>['provider'];
export interface Provider {
  id: string;
  name: string;
  description: string;
  // Some icons exist in ProviderIcon but aren't in the type definition yet
  icon?: string;
  available: boolean;
}

// Provider configuration with names, descriptions, and icons
// This mirrors the data from dashboard's pipes/providers.ts
export const PIPES_PROVIDERS: Provider[] = [{
  id: 'asana',
  name: 'Asana',
  description: 'Manage projects and tasks with connected team workflows.',
  icon: 'asana',
  available: true
}, {
  id: 'box',
  name: 'Box',
  description: 'Securely store, share, and collaborate on cloud files.',
  icon: 'box',
  available: true
}, {
  id: 'calendly',
  name: 'Calendly',
  description: 'Schedule and manage appointments, meetings, and events.',
  icon: 'calendly',
  available: true
}, {
  id: 'confluence',
  name: 'Confluence',
  description: 'Create, organize, and collaborate on team documentation.',
  icon: 'confluence',
  available: false
}, {
  id: 'dropbox',
  name: 'Dropbox',
  description: 'Sync, share, and back up files in the cloud.',
  icon: 'dropbox',
  available: true
}, {
  id: 'frame-io',
  name: 'Frame.io',
  description: 'Collaborate on video projects with review and approval workflows.',
  icon: 'frame-io',
  available: true
}, {
  id: 'front',
  name: 'Front',
  description: 'Manage customer communications and team collaboration in one inbox.',
  icon: 'front',
  available: true
}, {
  id: 'github',
  name: 'GitHub',
  description: 'Collaborate on code, issues, and pull requests securely.',
  icon: 'github',
  available: true
}, {
  id: 'gitlab',
  name: 'GitLab',
  description: 'Manage repositories, CI/CD pipelines, and code collaboration.',
  icon: 'gitlab',
  available: true
}, {
  id: 'gmail',
  name: 'Gmail',
  description: 'Access, send, and organize emails with Gmail integration.',
  icon: 'google-mail',
  available: true
}, {
  id: 'google',
  name: 'Google',
  description: 'Connect your Google account to integrate with Google services.',
  icon: 'google',
  available: true
}, {
  id: 'google-calendar',
  name: 'Google Calendar',
  description: 'Schedule events, manage time, and share availability.',
  icon: 'google-calendar',
  available: true
}, {
  id: 'google-drive',
  name: 'Google Drive',
  description: 'Store, share, and collaborate on cloud-based documents.',
  icon: 'google-drive',
  available: true
}, {
  id: 'helpscout',
  name: 'Help Scout',
  description: 'Deliver excellent customer support with help desk and knowledge base tools.',
  icon: 'helpscout',
  available: true
}, {
  id: 'hubspot',
  name: 'HubSpot',
  description: 'Track leads, manage marketing, and automate sales pipelines.',
  icon: 'hubspot',
  available: true
}, {
  id: 'intercom',
  name: 'Intercom',
  description: 'Engage customers with in-app messaging and support tools.',
  icon: 'intercom',
  available: true
}, {
  id: 'intralinks',
  name: 'Intralinks',
  description: 'Access virtual data rooms, documents, and deal information.',
  icon: 'intralinks',
  available: true
}, {
  id: 'jira',
  name: 'Jira',
  description: 'Track issues, bugs, and agile project workflows.',
  icon: 'jira',
  available: false
}, {
  id: 'linear',
  name: 'Linear',
  description: 'Streamline issue tracking and product development workflows.',
  icon: 'linear',
  available: true
}, {
  id: 'microsoft',
  name: 'Microsoft',
  description: 'Connect your Microsoft account to integrate with Microsoft services.',
  icon: 'microsoft',
  available: true
}, {
  id: 'microsoft-onedrive',
  name: 'Microsoft OneDrive',
  description: 'Store, sync, and share files across Microsoft cloud.',
  icon: 'microsoft-onedrive',
  available: true
}, {
  id: 'microsoft-onenote',
  name: 'Microsoft OneNote',
  description: 'Access and manage notebooks, sections, and notes.',
  icon: 'microsoft-onenote',
  available: true
}, {
  id: 'microsoft-outlook',
  name: 'Microsoft Outlook',
  description: 'Manage email, contacts, and communication seamlessly.',
  icon: 'microsoft-outlook',
  available: true
}, {
  id: 'microsoft-outlook-calendar',
  name: 'Microsoft Outlook Calendar',
  description: 'Schedule meetings and manage calendar events efficiently.',
  icon: 'microsoft-outlook-calendar',
  available: true
}, {
  id: 'microsoft-sharepoint',
  name: 'Microsoft SharePoint',
  description: 'Access and manage team sites, document libraries, and content.',
  icon: 'microsoft-sharepoint',
  available: true
}, {
  id: 'microsoft-teams',
  name: 'Microsoft Teams',
  description: 'Collaborate with teams, channels, chats, and meetings.',
  icon: 'microsoft-teams',
  available: true
}, {
  id: 'microsoft-todo',
  name: 'Microsoft To-Do',
  description: 'Manage tasks, to-do lists, and personal productivity.',
  icon: 'microsoft-todo',
  available: true
}, {
  id: 'notion',
  name: 'Notion',
  description: 'Organize notes, docs, and projects in one workspace.',
  icon: 'notion',
  available: true
}, {
  id: 'prefect',
  name: 'Prefect',
  description: 'Orchestrate and monitor data workflows and pipelines.',
  icon: 'prefect',
  available: false
}, {
  id: 'pydantic-logfire',
  name: 'Pydantic Logfire',
  description: 'Monitor, debug, and analyze logs with structured data.',
  icon: 'pydantic-logfire',
  available: false
}, {
  id: 'salesforce',
  name: 'Salesforce',
  description: 'Manage customer relationships, sales, and service operations.',
  icon: 'salesforce',
  available: true
}, {
  id: 'sentry',
  name: 'Sentry',
  description: 'Monitor, track, and resolve application errors in real-time.',
  icon: 'sentry',
  available: true
}, {
  id: 'slack',
  name: 'Slack',
  description: 'Communicate and collaborate with team channels and messages.',
  icon: 'slack',
  available: true
}, {
  id: 'snowflake',
  name: 'Snowflake',
  description: 'Access and query data from your Snowflake data warehouse.',
  icon: 'snowflake',
  available: true
}, {
  id: 'stripe',
  name: 'Stripe',
  description: 'Process payments, subscriptions, and financial transactions securely.',
  icon: 'stripe',
  available: false
}, {
  id: 'xero',
  name: 'Xero',
  description: 'Manage accounting, invoicing, and financial reporting for businesses.',
  icon: 'xero',
  available: true
}, {
  id: 'zendesk',
  name: 'Zendesk',
  description: 'Deliver customer support and manage service tickets.',
  icon: 'zendesk',
  available: false
}];
export const PipesProvidersTable = () => {
  // Filter to only available providers and sort alphabetically
  const providers = PIPES_PROVIDERS.filter(p => p.available).sort((a, b) => a.name.localeCompare(b.name));
  return <Table.Root data-sentry-element="Table.Root" data-sentry-component="PipesProvidersTable" data-sentry-source-file="pipes-providers-table.tsx">
      <Table.Content data-sentry-element="Table.Content" data-sentry-source-file="pipes-providers-table.tsx">
        <Table.Header data-sentry-element="Table.Header" data-sentry-source-file="pipes-providers-table.tsx">
          <Table.Row data-sentry-element="Table.Row" data-sentry-source-file="pipes-providers-table.tsx">
            <Table.ColumnHeader style={{
            width: 200
          }} data-sentry-element="Table.ColumnHeader" data-sentry-source-file="pipes-providers-table.tsx">
              Provider
            </Table.ColumnHeader>
            <Table.ColumnHeader style={{
            width: 180
          }} data-sentry-element="Table.ColumnHeader" data-sentry-source-file="pipes-providers-table.tsx">Slug</Table.ColumnHeader>
            <Table.ColumnHeader data-sentry-element="Table.ColumnHeader" data-sentry-source-file="pipes-providers-table.tsx">Description</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body data-sentry-element="Table.Body" data-sentry-source-file="pipes-providers-table.tsx">
          {providers.map(({
          id,
          name,
          description,
          icon
        }) => <Table.Row key={id}>
              <Table.RowHeader>
                <Flex align="center" gap="2">
                  {icon && <ProviderIcon
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              provider={icon as ProviderIconType} size="2" />}
                  <Text weight="medium">{name}</Text>
                </Flex>
              </Table.RowHeader>
              <Table.Cell>
                <CopyChip color="gray">
                  <Code variant="ghost">{id}</Code>
                </CopyChip>
              </Table.Cell>
              <Table.Cell>
                <Text color="gray">{description}</Text>
              </Table.Cell>
            </Table.Row>)}
        </Table.Body>
      </Table.Content>
    </Table.Root>;
};